import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { Spinner } from '@/components/primitives/Spinner';
import { Icon } from '@/components/Icon';
import { ApiError } from '@/api/client';
import { sendCode, verifyCode } from '@/api/opengater/auth';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import s from './OpengaterLogin.module.css';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function errorText(e, fallback) {
    if (e instanceof ApiError) {
        const body = e.body;
        const detail = body?.detail;
        if (typeof detail === 'string')
            return detail;
        if (e.status === 429)
            return 'Слишком много попыток. Попробуйте позже.';
        if (e.status === 404)
            return 'Пользователь с таким email не найден.';
        if (e.status === 400)
            return 'Неверный или просроченный код.';
        if (e.status === 0)
            return e.message;
    }
    return fallback;
}
export function OpengaterLogin() {
    const { sync } = useOgAuth();
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const codeRef = useRef(null);
    const emailValid = EMAIL_RE.test(email.trim());
    const codeValid = code.trim().length >= 4;
    async function onSendCode(e) {
        e?.preventDefault();
        if (!emailValid || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            await sendCode(email.trim());
            setStep('code');
            // Focus the code field once it mounts.
            setTimeout(() => codeRef.current?.focus(), 0);
        }
        catch (err) {
            setError(errorText(err, 'Не удалось отправить код. Проверьте соединение.'));
        }
        finally {
            setBusy(false);
        }
    }
    async function onVerify(e) {
        e.preventDefault();
        if (!codeValid || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            await verifyCode(email.trim(), code.trim());
            sync(); // hand off to the gate — it decides staff vs. plain user
        }
        catch (err) {
            setError(errorText(err, 'Не удалось войти. Проверьте код.'));
        }
        finally {
            setBusy(false);
        }
    }
    function backToEmail() {
        setStep('email');
        setCode('');
        setError(null);
    }
    return (_jsx("div", { className: s.wrap, children: _jsxs("div", { className: s.card, children: [_jsxs("div", { className: s.brand, children: [_jsx("img", { src: "/opengater-logo.png", alt: "", className: s.logo }), _jsxs("div", { className: s.brandText, children: [_jsx("div", { className: s.title, children: "Opengater Manager" }), _jsx("div", { className: s.subtitle, children: "\u041F\u0430\u043D\u0435\u043B\u044C \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F \u0438 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0438" })] })] }), step === 'email' ? (_jsxs("form", { className: s.form, onSubmit: onSendCode, noValidate: true, children: [_jsx("label", { className: s.label, htmlFor: "og-email", children: "\u0420\u0430\u0431\u043E\u0447\u0438\u0439 email" }), _jsx(Input, { id: "og-email", type: "email", inputMode: "email", autoComplete: "email", placeholder: "you@opengater.com", value: email, onChange: (e) => setEmail(e.target.value), autoFocus: true, disabled: busy }), error && _jsx("div", { className: s.error, children: error }), _jsx(Button, { type: "submit", size: "lg", disabled: !emailValid || busy, className: s.submit, children: busy ? _jsx(Spinner, { size: 16 }) : 'Получить код' }), _jsx("div", { className: s.hint, children: "\u041A\u043E\u0434 \u043F\u0440\u0438\u0434\u0451\u0442 \u043D\u0430 \u043F\u043E\u0447\u0442\u0443. \u0414\u043E\u0441\u0442\u0443\u043F \u0432 \u043F\u0430\u043D\u0435\u043B\u044C \u2014 \u0442\u043E\u043B\u044C\u043A\u043E \u0434\u043B\u044F \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u043E\u0432 \u0441 \u0440\u043E\u043B\u044C\u044E support \u0438\u043B\u0438 admin." })] })) : (_jsxs("form", { className: s.form, onSubmit: onVerify, noValidate: true, children: [_jsxs("div", { className: s.sentTo, children: ["\u041A\u043E\u0434 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D \u043D\u0430 ", _jsx("span", { className: s.sentEmail, children: email.trim() }), _jsx("button", { type: "button", className: s.changeLink, onClick: backToEmail, children: "\u0438\u0437\u043C\u0435\u043D\u0438\u0442\u044C" })] }), _jsx("label", { className: s.label, htmlFor: "og-code", children: "\u041A\u043E\u0434 \u0438\u0437 \u043F\u0438\u0441\u044C\u043C\u0430" }), _jsx(Input, { id: "og-code", ref: codeRef, inputMode: "numeric", autoComplete: "one-time-code", placeholder: "000000", mono: true, value: code, onChange: (e) => setCode(e.target.value.replace(/\s+/g, '')), disabled: busy }), error && _jsx("div", { className: s.error, children: error }), _jsx(Button, { type: "submit", size: "lg", disabled: !codeValid || busy, className: s.submit, children: busy ? _jsx(Spinner, { size: 16 }) : 'Войти' }), _jsxs("button", { type: "button", className: s.resend, onClick: () => void onSendCode(), disabled: busy, children: [_jsx(Icon, { name: "refresh", size: 13 }), " \u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043A\u043E\u0434 \u043F\u043E\u0432\u0442\u043E\u0440\u043D\u043E"] })] }))] }) }));
}
